<template>
  <div v-if="!dismissed" class="app-alert" :class="[toneClass, variantClass]" role="status">
    <icon
      v-if="resolvedIcon"
      :path="resolvedIcon"
      size="20"
      class="app-alert__icon"
      aria-hidden="true"
    />
    <div class="app-alert__body">
      <p v-if="title" class="app-alert__title">{{ title }}</p>
      <div class="app-alert__content"><slot /></div>
    </div>
    <div v-if="$slots.actions" class="app-alert__actions">
      <slot name="actions" />
    </div>
    <button
      v-else-if="dismissible"
      type="button"
      class="app-alert__dismiss"
      :aria-label="dismissLabel"
      @click="dismiss"
    >
      <icon :path="mdiClose" size="16" />
    </button>
  </div>
</template>

<script setup>
  /**
   * AppAlert — inline contextual banner (info / success / warning / danger).
   *
   * Replaces the legacy `Notification` (dismissible tinted banner, `@/colors`)
   * and `ExplanationBox` (`variant="left-accent"`). Tones map to the semantic
   * state tokens so light/dark are theme-driven. Use for sentence-length
   * messaging; for short status words use `AppBadge`.
   *
   * Dismiss behaviour mirrors the old Notification: clicking the close button
   * hides the alert locally and emits `dismiss`. Provide an `#actions` slot to
   * replace the close button with custom controls.
   */
  import Icon from '@/components/Icon';
  import {
    mdiAlertCircleOutline,
    mdiAlertOutline,
    mdiCheckCircleOutline,
    mdiClose,
    mdiInformationOutline,
  } from '@mdi/js';
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  defineOptions({ name: 'AppAlert' });

  const props = defineProps({
    /** Semantic colour. */
    tone: {
      type: String,
      default: 'info',
      validator: (v) => ['neutral', 'brand', 'success', 'warning', 'danger', 'info'].includes(v),
    },
    /** soft (tinted bg), outline (border only), or left-accent (ExplanationBox style). */
    variant: {
      type: String,
      default: 'soft',
      validator: (v) => ['soft', 'outline', 'left-accent'].includes(v),
    },
    /** Optional bold heading above the message. */
    title: { type: String, default: '' },
    /**
     * Leading icon path. Pass `false`/'' to hide; omit to use the tone's
     * default icon.
     */
    icon: { type: [String, Boolean], default: undefined },
    /** Show a close button that hides the alert and emits `dismiss`. */
    dismissible: { type: Boolean, default: false },
    /** Accessible label for the close button. */
    dismissLabel: { type: String, default: '' },
  });

  const emit = defineEmits(['dismiss']);

  let t = (key, fallback) => fallback ?? key;
  try {
    const i18n = useI18n();
    if (i18n && typeof i18n.t === 'function') t = i18n.t;
  } catch {
    // useI18n throws outside a provider (some tests) — keep the fallback.
  }

  const dismissed = ref(false);

  function dismiss() {
    dismissed.value = true;
    emit('dismiss');
  }

  const DEFAULT_ICONS = {
    neutral: mdiInformationOutline,
    brand: mdiInformationOutline,
    info: mdiInformationOutline,
    success: mdiCheckCircleOutline,
    warning: mdiAlertOutline,
    danger: mdiAlertCircleOutline,
  };

  const resolvedIcon = computed(() => {
    if (props.icon === false || props.icon === '') return '';
    if (typeof props.icon === 'string') return props.icon;
    return DEFAULT_ICONS[props.tone] || mdiInformationOutline;
  });

  const TONE_SOFT = {
    neutral: 'bg-theme-bg-subtle text-theme-text',
    brand: 'bg-theme-brand/10 text-theme-text',
    info: 'bg-theme-info/10 text-theme-text',
    success: 'bg-theme-success/10 text-theme-text',
    warning: 'bg-theme-warning/10 text-theme-text',
    danger: 'bg-theme-danger/10 text-theme-text',
  };

  const TONE_OUTLINE = {
    neutral: 'border border-theme-border text-theme-text',
    brand: 'border border-theme-brand/40 text-theme-text',
    info: 'border border-theme-info/40 text-theme-text',
    success: 'border border-theme-success/40 text-theme-text',
    warning: 'border border-theme-warning/40 text-theme-text',
    danger: 'border border-theme-danger/40 text-theme-text',
  };

  const TONE_ACCENT = {
    neutral: 'bg-theme-bg-subtle border-theme-border text-theme-text',
    brand: 'bg-theme-brand/5 border-theme-brand text-theme-text',
    info: 'bg-theme-info/5 border-theme-info text-theme-text',
    success: 'bg-theme-success/5 border-theme-success text-theme-text',
    warning: 'bg-theme-warning/5 border-theme-warning text-theme-text',
    danger: 'bg-theme-danger/5 border-theme-danger text-theme-text',
  };

  const variantClass = computed(() => {
    if (props.variant === 'outline') return TONE_OUTLINE[props.tone];
    if (props.variant === 'left-accent') return ['app-alert--accent', TONE_ACCENT[props.tone]];
    return TONE_SOFT[props.tone];
  });

  // The icon keeps the tone colour even when the body text is neutral; the
  // colour itself is applied via the `app-alert--icon-{tone}` scoped rule.
  const toneClass = computed(() => `app-alert--icon-${props.tone}`);
</script>

<style scoped>
  .app-alert {
    @apply flex items-start gap-3 rounded-theme-md px-4 py-3 text-sm leading-5;
  }

  .app-alert--accent {
    @apply rounded-l-none border-l-4;
  }

  .app-alert__icon {
    @apply mt-0.5 shrink-0;
  }

  .app-alert--icon-brand .app-alert__icon {
    color: rgb(var(--color-brand-primary));
  }
  .app-alert--icon-info .app-alert__icon {
    color: rgb(var(--color-state-info));
  }
  .app-alert--icon-success .app-alert__icon {
    color: rgb(var(--color-state-success));
  }
  .app-alert--icon-warning .app-alert__icon {
    color: rgb(var(--color-state-warning));
  }
  .app-alert--icon-danger .app-alert__icon {
    color: rgb(var(--color-state-danger));
  }
  .app-alert--icon-neutral .app-alert__icon {
    color: rgb(var(--color-text-muted));
  }

  .app-alert__body {
    @apply min-w-0 flex-1;
  }

  .app-alert__title {
    @apply mb-0.5 font-semibold;
  }

  .app-alert__actions {
    @apply ml-2 flex shrink-0 items-center gap-2;
  }

  .app-alert__dismiss {
    @apply -mr-1 ml-2 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-theme-sm text-theme-text-muted transition hover:bg-theme-bg-subtle hover:text-theme-text;
  }
</style>
